// v2 beech — a cathedral beech: a tall smooth silver-grey column rising from a
// flared root collar, splitting high up into two ascending branches that hold a
// high domed crown (a dark under-storey lobe, a ring of three mid lobes, one
// broad dome and a bright cap) carried well clear of the ground. Variants
// re-spin the ring, the fork and the palette deterministically. Ported 1:1 from
// age-of-mcp's v2/tree-beech.ts, using the library's shared `tree` (h01, lobe,
// pick) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { deep: "#3c7032", mid: "#4f8f3e", lite: "#62a64c", top: "#79bc59" },
  { deep: "#386b36", mid: "#4a8a42", lite: "#5da050", top: "#72b55e" },
  { deep: "#41732e", mid: "#549238", lite: "#68a844", top: "#7fbe52" },
] as const;

const TRUNK = "#9a948a";
const TRUNK_DARK = "#7e786d";

function beechParts(v: number): Part[] {
  const seed = 61 + v * 11;
  const yaw = v * 2.5;
  const lean = (tree.h01(seed, 0) - 0.5) * 0.08;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // flared root collar + the long smooth grey column
    { geometry: new THREE.CylinderGeometry(0.12, 0.18, 0.18, 7),
      color: TRUNK_DARK, position: [0, 0.09, 0] },
    { geometry: new THREE.CylinderGeometry(0.075, 0.115, 1.05, 7),
      color: TRUNK, position: [0, 0.6, 0], rotation: [0, 0, lean] },
    // the high fork: two ascending branches into the dome
    { geometry: new THREE.CylinderGeometry(0.035, 0.055, 0.5, 5),
      color: TRUNK, position: [-0.1, 1.3, 0.05], rotation: [0.15, 0, 0.35] },
    { geometry: new THREE.CylinderGeometry(0.035, 0.055, 0.48, 5),
      color: TRUNK_DARK, position: [0.11, 1.32, -0.04], rotation: [-0.12, 0, -0.38] },
    // dark under-storey lobe — the shaded belly of the high dome
    tree.lobe(seed, 10, 0, 1.28, 0, 0.46, pal.deep, 0.55),
  ];
  // a ring of three mid lobes around the dome's waist
  for (let k = 0; k < 3; k++) {
    const ang = yaw + k * 2.1 + tree.h01(seed, 20 + k) * 0.6;
    const d = 0.26 + tree.h01(seed, 24 + k) * 0.08;
    const r = 0.29 + tree.h01(seed, 28 + k) * 0.05;
    parts.push(tree.lobe(seed, 32 + k,
      Math.cos(ang) * d, 1.45 + tree.h01(seed, 36 + k) * 0.05, Math.sin(ang) * d,
      r, k % 2 === 0 ? pal.mid : pal.lite, 0.75));
  }
  // the broad high dome + a bright crown cap
  parts.push(tree.lobe(seed, 40, 0, 1.66, 0, 0.5, pal.mid, 0.78));
  parts.push(tree.lobe(seed, 41, 0.06, 1.9, 0.04, 0.22, pal.top, 0.7));
  return parts;
}

export const treeBeechV2 = defineModel({
  id: "tree-beech-v2",
  batchId: "fable",
  name: "Tree beech (v2)",
  tags: ["scenery","tree","beech","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree beech.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.6,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => beechParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => beechParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => beechParts(2) },
  ],
  animations: [],
});
